import { get } from 'svelte/store';
import { track } from '../../../../utils/track/track';
import { filterStores } from '../../state';
import { getActiveArtist } from '../getActiveArtist/getActiveArtist';
import {
	FilterTrackEvent,
	getFilterTrackEvents
} from '../getFilterTrackEvents/getFilterTrackEvents';
import { getParamsFromFilter } from '../getParamsFromFilter/getParamsFromFilter';
import { getSearchParamsObject } from '../getSearchParamsObject/getSearchParamsObject';
import { getSearchParamsSortObject } from '../getSearchParamsSortObject/getSearchParamsSortObject';
import { scrollToFilterResults } from '../scrollToFilterResults';
import { goto } from '$app/navigation';
import { page } from '$app/state';

export const applyFilters = (options?: {
	sort?: Record<string, string>;
	retainSearchParams?: boolean;
	scrollTarget?: string;
}) => {
	const { filterStore, touchedItemsStore, pageParamsStore, filterSectionsStore, trackPrefixStore } =
		filterStores;

	const searchParams = page.url.searchParams;

	const pageParams = get(pageParamsStore);

	const pageParamsObject = pageParams.reduce(
		(acc, param) => {
			const value = searchParams.get(param);

			if (value) {
				return {
					...acc,
					[param]: value
				};
			}

			return acc;
		},
		{} as Record<string, string>
	);

	const existingParamsObject = options?.retainSearchParams
		? {
				...getSearchParamsObject(searchParams),
				...pageParamsObject
			}
		: {
				...pageParamsObject
			};

	const existingSortParamsObject = getSearchParamsSortObject(searchParams);

	const filter = get(filterStore);
	const touchedFilterItems = get(touchedItemsStore);

	const filterParams = getParamsFromFilter({
		filter,
		touchedFilterItems
	});

	const obj = {
		...existingParamsObject,
		...existingSortParamsObject,
		...options?.sort,
		...filterParams
	} as Record<string, string>;

	const searchParamsString = new URLSearchParams(obj).toString();

	const href = searchParamsString ? `?${searchParamsString}` : '?';

	// Tracking is fire-and-forget on purpose: never await it, so a slow or
	// failed analytics call cannot delay or break the navigation below.
	const trackPrefix = get(trackPrefixStore);

	if (trackPrefix) {
		const filterSections = get(filterSectionsStore);

		if (options?.sort) {
			// A sort is applied on top of whatever filters are active, so the
			// artist narrowing the grid rides along as a cross-cutting dimension.
			const artist = getActiveArtist({ filter, filterSections });

			Object.entries(options.sort).forEach(([sortParam, sortValue]) => {
				track(`${trackPrefix}/${FilterTrackEvent.SortChanged}`, {
					sort_by: sortValue,
					sort_index: Number(sortParam.replace('sort', '')) || 0,
					...(artist ? { artist } : {})
				});
			});
		} else {
			getFilterTrackEvents({
				filter,
				touchedFilterItems,
				filterSections
			}).forEach(({ event, props }) => {
				track(`${trackPrefix}/${event}`, props);
			});
		}
	}

	return Promise.resolve(goto(href, { reset: false })).then(() => {
		scrollToFilterResults(options?.scrollTarget);
	});
};
